import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Portal, Slide } from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import CustomSnackbar from '@components/CustomSnackbar';
import If from '@components/If';
import NotificationAlert from '@components/NotificationAlert';
import SxDef from '@core/types/SxDef';

export type SnackbarType = AlertColor | 'default';

export interface Notification {
  id: string;
  message: string;
  title: string;
  type: AlertColor;
  autoHideDuration?: number;
  onClose?: VoidFunction;
}

export interface ISnackbarContext {
  setOpen: (open: boolean) => void;
  setMessage: (message: string) => void;
  setType: (type: SnackbarType) => void;
  setTitle: (title: string) => void;
  setAutoHideDuration: (duration: number) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const defaultSnackbarContext: ISnackbarContext = {} as any;
export const SnackbarContext = createContext<ISnackbarContext>(defaultSnackbarContext);

let notificationId = 0;
const AUTO_DISMISS_MS = 2000;
const EXIT_ANIMATION_MS = 500;

const sx: SxDef = {
  root: {
    position: 'fixed',
    top: 80,
    right: 0,
    zIndex: 1400,
    display: 'flex',
    flexDirection: 'column',
    width: 360,
    maxWidth: '100vw',
    maxHeight: 'calc(100vh - 96px)',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
};

const SnackbarProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<SnackbarType>('default');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [autoHideDuration, setAutoHideDuration] = useState(5000);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [exiting, setExiting] = useState<Set<string>>(new Set());
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const removeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const notificationsRef = useRef<Notification[]>([]);
  notificationsRef.current = notifications;

  const handleClose = () => {
    setOpen(false);
  };

  const startExit = useCallback((id: string) => {
    if (removeTimersRef.current.has(id)) return;
    setExiting((prev) => new Set(prev).add(id));
    const removeTimer = setTimeout(() => {
      notificationsRef.current.find((n) => n.id === id)?.onClose?.();
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setExiting((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      removeTimersRef.current.delete(id);
    }, EXIT_ANIMATION_MS);
    removeTimersRef.current.set(id, removeTimer);
  }, []);

  const scheduleAutoDismiss = useCallback(
    (id: string, duration = AUTO_DISMISS_MS) => {
      const timer = setTimeout(() => {
        dismissTimersRef.current.delete(id);
        startExit(id);
      }, duration);
      dismissTimersRef.current.set(id, timer);
    },
    [startExit],
  );

  const addNotification = useCallback(
    (notification: Omit<Notification, 'id'>) => {
      const id = String(++notificationId);
      setNotifications((prev) => [...prev, { ...notification, id }]);
      scheduleAutoDismiss(id, notification.autoHideDuration);
    },
    [scheduleAutoDismiss],
  );

  const removeNotification = useCallback(
    (id: string) => {
      const autoTimer = dismissTimersRef.current.get(id);
      if (autoTimer) {
        clearTimeout(autoTimer);
        dismissTimersRef.current.delete(id);
      }
      startExit(id);
    },
    [startExit],
  );

  const clearAllNotifications = useCallback(() => {
    dismissTimersRef.current.forEach((timer) => clearTimeout(timer));
    dismissTimersRef.current.clear();
    removeTimersRef.current.forEach((timer) => clearTimeout(timer));
    removeTimersRef.current.clear();
    notificationsRef.current.forEach((n) => n.onClose?.());
    setNotifications([]);
    setExiting(new Set());
  }, []);

  const pauseAutoDismiss = useCallback((id: string) => {
    const timer = dismissTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimersRef.current.delete(id);
    }
  }, []);

  const resumeAutoDismiss = useCallback(
    (notification: Notification) => {
      const { id } = notification;
      if (!dismissTimersRef.current.has(id) && !removeTimersRef.current.has(id)) {
        scheduleAutoDismiss(id, notification.autoHideDuration);
      }
    },
    [scheduleAutoDismiss],
  );

  useEffect(() => {
    const dismissTimers = dismissTimersRef.current;
    const removeTimers = removeTimersRef.current;
    return () => {
      dismissTimers.forEach((timer) => clearTimeout(timer));
      removeTimers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const value = useMemo(
    () => ({
      setOpen,
      setMessage,
      setType,
      setTitle,
      setAutoHideDuration,
      addNotification,
      removeNotification,
      clearAllNotifications,
    }),
    [addNotification, removeNotification, clearAllNotifications],
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {type === 'default' ? (
        <CustomSnackbar
          open={open}
          autoHideDuration={autoHideDuration}
          onClose={handleClose}
          message={message}
          title={''}
          type={'info'}
        />
      ) : (
        <CustomSnackbar
          open={open}
          autoHideDuration={autoHideDuration}
          onClose={handleClose}
          message={message}
          type={type}
          title={title}
        />
      )}
      <If condition={notifications.length > 0}>
        <Portal>
          <Box sx={sx.root}>
            <Box sx={sx.container}>
              {notifications.map((notification) => (
                <Slide
                  key={notification.id}
                  in={!exiting.has(notification.id)}
                  mountOnEnter
                  unmountOnExit
                  timeout={EXIT_ANIMATION_MS}
                  direction="left"
                >
                  <Box
                    onMouseEnter={() => pauseAutoDismiss(notification.id)}
                    onMouseLeave={() => resumeAutoDismiss(notification)}
                  >
                    <NotificationAlert notification={notification} onClose={removeNotification} />
                  </Box>
                </Slide>
              ))}
            </Box>
          </Box>
        </Portal>
      </If>
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
